import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAppTheme } from '../../hooks/useAppTheme';

export interface LastSyncProps {
  lastSyncAt: Date | number | null;
  error?: boolean;
  onRetry?: () => void;
  prefix?: string;
  sx?: any;
}

const formatAge = (ms: number): string => {
  if (ms < 0) return 'now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const LastSync: React.FC<LastSyncProps> = ({ lastSyncAt, error, onRetry, prefix = 'Last sync', sx }) => {
  const { C } = useAppTheme();
  const [, setTick] = useState(0);

  useEffect(() => {
    if (lastSyncAt == null) return;
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [lastSyncAt]);

  if (lastSyncAt == null && !error) return null;

  const ts = typeof lastSyncAt === 'number' ? lastSyncAt : lastSyncAt?.getTime() ?? 0;
  const age = ts ? Date.now() - ts : 0;
  const text = ts ? `${prefix} ${formatAge(age)}` : `${prefix} —`;
  const absolute = ts ? new Date(ts).toLocaleString() : 'never';

  return (
    <Tooltip arrow title={error ? 'Last refresh failed' : absolute}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ...sx }}>
        {error && <ErrorOutlineIcon sx={{ fontSize: 12, color: C.bear }} />}
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.66rem',
            color: error ? C.bear : C.dim,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}
        >
          {error ? 'Refresh failed' : text}
        </Typography>
        {onRetry && (
          <IconButton
            size="small"
            onClick={onRetry}
            sx={{ p: 0.25, color: error ? C.bear : C.dim }}
            aria-label="Retry"
          >
            <RefreshIcon sx={{ fontSize: 12 }} />
          </IconButton>
        )}
      </Box>
    </Tooltip>
  );
};

export default LastSync;
